/* eslint-disable max-lines */

import { useGetTwilioCallDetails } from '@hakimo-ui/hakimo/data-access';
import {
  AlarmTalkdownState,
  AlarmUpdate,
  CallTimeRangeProps,
  EscalationEvent,
  LocationAlarmUpdate,
  TalkdownStatus,
  TalkdownType,
} from '@hakimo-ui/hakimo/types';
import { Alert, Button } from '@hakimo-ui/shared/ui-base';
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import MiniAudioPlayer from '../audio-recorder/mini-audio-player/MiniAudioPlayer';

dayjs.extend(utc);
dayjs.extend(timezone);

const CALL_TIME_DATE_FORMAT = 'DD/MM/YYYY, HH:mm:ss';
const CALL_TIME_TIME_FORMAT = 'HH:mm:ss';

export const CallTimeRange = (props: CallTimeRangeProps) => {
  const { twilioSsid, fallbackTime, locationTimezone } = props;
  const { data } = useGetTwilioCallDetails(twilioSsid);

  if (!data || !data.startTime || !data.endTime) {
    return <>{fallbackTime}</>;
  }

  const start = locationTimezone
    ? dayjs(data.startTime).tz(locationTimezone)
    : dayjs(data.startTime);
  const end = locationTimezone
    ? dayjs(data.endTime).tz(locationTimezone)
    : dayjs(data.endTime);

  return (
    <>{`${start.format(CALL_TIME_DATE_FORMAT)} - ${end.format(
      CALL_TIME_TIME_FORMAT
    )}`}</>
  );
};

export const getCallTimeRangeNode = (
  ssid: string,
  fallbackTime: string,
  locationTimezone?: string
) => {
  return (
    <CallTimeRange
      twilioSsid={ssid}
      fallbackTime={fallbackTime}
      locationTimezone={locationTimezone}
    />
  );
};

export function getTalkdownStatusIconNode(status: string) {
  let icon;
  switch (status) {
    case 'success':
    case TalkdownStatus.SUCCESS:
      icon = (
        <CheckCircleIcon className="h-5 w-5 text-green-400 dark:text-green-200" />
      );
      break;
    case 'warning':
      icon = (
        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 dark:text-yellow-200" />
      );
      break;
    case 'error':
    case TalkdownStatus.FAILED:
      icon = <XCircleIcon className="h-5 w-5 text-red-400 dark:text-red-300" />;
      break;
    default:
      icon = null;
  }
  return icon;
}

interface BasicColumn {
  id: string;
  name: string;
}
export const talkdownDetailsModalAllColumns: BasicColumn[] = [
  {
    id: 'rawAlarmId',
    name: 'Raw Alarm Id',
  },
  {
    id: 'rawAlarmAddedTime',
    name: 'Raw Alarm Added Time',
  },
  {
    id: 'status',
    name: 'Talkdown Status',
  },
  {
    id: 'ingestedTalkdown',
    name: 'Relayed Talkdown',
  },
  {
    id: 'talkdownTime',
    name: 'Talkdown Completed Time',
  },
  {
    id: 'comments',
    name: 'Comments',
  },
];

export const getColumnNode = (columns: BasicColumn[]) => {
  return columns.map((col) => (
    <div
      key={col.id}
      className="dark:text-ondark-text-1 font-bold text-gray-600"
    >
      {col.name}
    </div>
  ));
};

export function getTalkdownExtraDetailsNode(update: AlarmUpdate) {
  const talkdownState = update?.talkdown?.[0];
  if (!talkdownState) {
    return null;
  }
  let message = '';
  const status = talkdownState.status;
  switch (status) {
    case TalkdownStatus.SUCCESS:
      message = 'Talkdown transmitted successfully';
      break;
    case TalkdownStatus.FAILED:
      message = 'Talkdown tranmission failed';
      break;
    case TalkdownStatus.PENDING:
      message = 'Talkdown yet to be relayed';
      break;
  }
  return (
    <div className="flex items-center gap-2 text-xs">
      {message}
      {getTalkdownStatusIconNode(status)}
    </div>
  );
}

export function getTalkdownExtraDetailsModalContent(update: AlarmUpdate) {
  const talkdownState = update?.talkdown?.[0];
  if (!talkdownState) {
    return null;
  }
  const columns = talkdownDetailsModalAllColumns.filter(
    (col) => !['rawAlarmId', 'rawAlarmAddedTime'].includes(col.id)
  );
  return (
    <div className="grid grid-cols-4 gap-2 text-sm">
      {getColumnNode(columns)}
      <span className="flex items-center gap-2">
        {getTalkdownStatusIconNode(talkdownState.status)}
        {talkdownState.status}
      </span>
      <span>
        {talkdownState.ingested_talkdown_audio ? (
          <span className="flex">
            <MiniAudioPlayer
              src={talkdownState.ingested_talkdown_audio}
              isLoading={false}
            />
          </span>
        ) : (
          'NA'
        )}
      </span>
      <span>
        {talkdownState.talkdown_completed_utc
          ? dayjs(new Date(talkdownState.talkdown_completed_utc)).format(
              'DD/MM/YYYY, HH:mm:ss'
            )
          : '-'}
      </span>

      <span>{talkdownState.custom_message || '-'}</span>
    </div>
  );
}

const getFormattedDate = (date?: string) => {
  if (!date) {
    return '-';
  }
  const dateObj = new Date(date);
  return dayjs(dateObj).format('DD/MM/YYYY, HH:mm:ss');
};

export function getNewAlarmEventDetailsNode(
  events: LocationAlarmUpdate[],
  talkdownsState: AlarmTalkdownState[]
) {
  const allEventsCount = events.length;
  const noRawAlarmId = events.every((ev) => !ev.raw_alarm_id);
  const automatedTalkdownState = talkdownsState.filter(
    (state) => state.talkdown_type === TalkdownType.AUTOMATED
  );
  if (noRawAlarmId || automatedTalkdownState.length === 0) {
    return null;
  }

  let eventTalkdownSuccessCount = 0;
  for (let i = 0; i < events.length; i++) {
    const rawAlarmId = events[i].raw_alarm_id;
    const isTalkdownSuccess = automatedTalkdownState.some(
      (talkdown) =>
        talkdown.raw_alarm_id === rawAlarmId &&
        talkdown.status === TalkdownStatus.SUCCESS
    );
    isTalkdownSuccess && eventTalkdownSuccessCount++;
  }
  let message = '';
  let type = 'info';
  if (allEventsCount === eventTalkdownSuccessCount) {
    message = `${
      allEventsCount > 1 ? 'All ' : ''
    }Automated talkdown were succcssfully transmitted`;
    type = 'success';
  } else if (eventTalkdownSuccessCount === 0) {
    message = `${
      allEventsCount > 1 ? 'All ' : ''
    }Automated talkdown tranmission failed`;
    type = 'error';
  } else if (allEventsCount > eventTalkdownSuccessCount) {
    message = `${
      allEventsCount > 1 ? 'Some ' : ''
    }Automated talkdowns tranmission failed`;
    type = 'warning';
  }

  const node = (
    <div className="flex items-center gap-2 text-xs">
      {message}
      {getTalkdownStatusIconNode(type)}
    </div>
  );
  return node;
}

interface EventTalkdownDetails {
  rawAlarmId?: string;
  rawAlarmAddedTime: string;
  ingestedTalkdownAudio: string;
  customMessage: string;
  status: TalkdownStatus;
  sentTalkdownAudio: string;
  completedTimeUtc: string;
}

export function getNewAlarmEventDetailsModalContentNode(
  events: LocationAlarmUpdate[],
  talkdownsState: AlarmTalkdownState[],
  isSupportRole?: boolean
) {
  const noRawAlarmId = events.every((ev) => !ev.raw_alarm_id);

  const automatedTalkdownState = talkdownsState.filter(
    (state) => state.talkdown_type === TalkdownType.AUTOMATED
  );
  if (noRawAlarmId || automatedTalkdownState.length === 0) {
    return null;
  }
  const eventTalkdownDetails: EventTalkdownDetails[] = [];
  events.reduce((acc, ev) => {
    const rawAlarmId = ev.raw_alarm_id;
    const alarmTalkdownState = automatedTalkdownState.find(
      (talkdownState) => talkdownState.raw_alarm_id === rawAlarmId
    );
    if (alarmTalkdownState) {
      acc.push({
        rawAlarmId: alarmTalkdownState?.raw_alarm_id,
        rawAlarmAddedTime: ev.update_time,
        ingestedTalkdownAudio: alarmTalkdownState.ingested_talkdown_audio,
        customMessage: alarmTalkdownState.custom_message,
        status: alarmTalkdownState.status,
        sentTalkdownAudio: alarmTalkdownState.talkdown_audio,
        completedTimeUtc: alarmTalkdownState.talkdown_completed_utc,
      });
    }
    return acc;
  }, eventTalkdownDetails);

  const onClickRawAlarmId = (alarmId?: string) => () => {
    if (isSupportRole && alarmId) {
      const url = `/alarms/${alarmId}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const columns = talkdownDetailsModalAllColumns;

  return (
    <div className="text-sm">
      <div className="grid grid-cols-6 gap-2">{getColumnNode(columns)}</div>
      <div className="max-h-[70vh] overflow-auto">
        {eventTalkdownDetails.map((det) => (
          <div
            key={det.rawAlarmId}
            className="hover:bg-onlight-bg-2 dark:hover:bg-ondark-bg-2 grid grid-cols-6 gap-2 rounded py-6"
          >
            <div className="flex items-center gap-2">
              <span className="w-[8rem] overflow-hidden text-ellipsis whitespace-nowrap">
                {det.rawAlarmId}
              </span>
              {isSupportRole && (
                <Button
                  variant="icon"
                  onClick={onClickRawAlarmId(det.rawAlarmId)}
                >
                  <ArrowTopRightOnSquareIcon className="text-primary-400 h-3 w-3" />
                </Button>
              )}
            </div>
            <span>{getFormattedDate(det.rawAlarmAddedTime)}</span>
            <span className="flex gap-2">
              {getTalkdownStatusIconNode(det.status)}
              {det.status}
            </span>
            <span>
              {det.ingestedTalkdownAudio ? (
                <span className="flex">
                  <MiniAudioPlayer
                    src={det.ingestedTalkdownAudio}
                    isLoading={false}
                  />
                </span>
              ) : (
                'NA'
              )}
            </span>
            <span>{getFormattedDate(det.completedTimeUtc)}</span>
            <span>{det.customMessage || '-'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function getManualTalkdownDetailsNode(
  update: EscalationEvent,
  talkdownsStates: AlarmTalkdownState[]
) {
  let updateAudioUrl = update.audio_url;
  if (!updateAudioUrl) {
    return null;
  }
  updateAudioUrl = updateAudioUrl.split('?')[0];
  const manualTalkdownState = talkdownsStates.find(
    (state) =>
      state.talkdown_type === TalkdownType.MANUAL &&
      updateAudioUrl &&
      state.talkdown_audio.includes(updateAudioUrl)
  );

  const talkdownStatus = manualTalkdownState?.status;
  let message = '';
  let icon = null;
  switch (talkdownStatus) {
    case TalkdownStatus.SUCCESS:
      message = 'Talkdown transmitted successfully';
      icon = getTalkdownStatusIconNode('success');
      break;
    case TalkdownStatus.PENDING:
      message = 'Talkdown yet to be relayed';
      break;
    case TalkdownStatus.FAILED:
      message = 'Talkdown tranmission failed';
      icon = getTalkdownStatusIconNode('error');
      break;
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      {message}
      {icon}
    </div>
  );
}

export function getManualTalkdownModalContentNode(
  update: EscalationEvent,
  talkdownsStates: AlarmTalkdownState[]
) {
  let updateAudioUrl = update.audio_url;
  if (!updateAudioUrl) {
    return null;
  }
  updateAudioUrl = updateAudioUrl.split('?')[0].split('audio')[1];
  if (!updateAudioUrl) {
    return;
  }
  const manualTalkdownState = talkdownsStates.find(
    (state) =>
      state.talkdown_type === TalkdownType.MANUAL &&
      updateAudioUrl &&
      state.talkdown_audio.includes(updateAudioUrl)
  );
  if (!manualTalkdownState) {
    return null;
  }

  const columns = talkdownDetailsModalAllColumns.filter(
    (col) => !['rawAlarmId', 'rawAlarmAddedTime'].includes(col.id)
  );

  return (
    <div className="grid grid-cols-4 gap-2 text-sm">
      {getColumnNode(columns)}
      <span className="flex gap-2">
        {getTalkdownStatusIconNode(manualTalkdownState.status)}
        {manualTalkdownState.status}
      </span>
      <span>
        {manualTalkdownState.ingested_talkdown_audio ? (
          <span className="flex">
            <MiniAudioPlayer
              src={manualTalkdownState.ingested_talkdown_audio}
              isLoading={false}
            />
          </span>
        ) : (
          'NA'
        )}
      </span>
      <span>
        {getFormattedDate(manualTalkdownState.talkdown_completed_utc)}
      </span>

      <span>{manualTalkdownState.custom_message || '-'}</span>
    </div>
  );
}

export function getTwilioCallErrorFormatter() {
  return (error: Error) => {
    const errorMessage = error.message?.toLowerCase() || '';
    if (errorMessage.includes('unknown exception')) {
      return (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Call recording will be available after the call ends.
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-screen-2xl p-6">
        <Alert type="error">{error.message ?? ''}</Alert>
      </div>
    );
  };
}
